import { AddressType } from '../../../Types/AddressType';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import StackHorizontal from '../../../Framework/Components/Wrappers/StackHorizontal';
import { ColourMod, ColourSolid, FlexAlign, FlexGap, FlexJustify } from '../../../Framework/Data/Enums/LayoutEnums';
import Button from '../../../Framework/Components/Buttons/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLocationDot, faPhone, faTrash, faUser } from '@fortawesome/pro-solid-svg-icons';
import { FormWrapperLabel } from '../../../Framework/Components/Forms/FormEntryWrapper/FormEntryWrapper';
import CountryCodeControl, { CountryCodeEntryType } from '../../Form/CountryCode';
import EmailControl from '../../../Framework/Components/Forms/Email/EmailControl';
import { useLayout } from '../../../Framework/Providers/LayoutProvider';
import Pill from '../../../Framework/Components/Pills/Pill';
import { PillSize } from '../../../Framework/Components/Pills/PillTypes';
import StackVertical from '../../../Framework/Components/Wrappers/StackVertical';
import TelephoneControl from '../../../Framework/Components/Forms/Telephone/TelephoneControl';

interface AddressImportRow extends AddressType {
	id: string;
	validRow: boolean;
	fieldIndex: number;
	removeCallback: any;
	lookUpsActive: (value: boolean) => void;
	embedded?: boolean;
}

const AddressImporterRow = (props: AddressImportRow) => {
	const { isMedium, isLarge, isXL } = useLayout();
	const { control, getValues, trigger, register, setError, clearErrors, formState } = useFormContext();

	const [editing, setEditing] = useState<boolean>(!props.validRow);

	const HandleRemove = (id: number) => {
		props.removeCallback(id);
	};

	useEffect(() => {
		trigger();
	}, []);

	if (!editing) {
		return (
			<div
				className={`item-row item-row-static item-row-table ${isLarge ? '' : 'mt-xs'}`}
				style={{ gridTemplateColumns: !isLarge ? '1fr auto' : '1fr auto', alignItems: 'center' }}
				data-row-id={props.fieldIndex}
			>
				<div
					style={{
						display: 'grid',
						gap: 'var(--sm-unit)',
						gridTemplateColumns: isXL ? 'repeat(4, 1fr)' : isMedium ? 'repeat(2, 1fr)' : 'repeat(1, 1fr)',
						alignItems: 'center',
					}}
				>
					<div>
						<FontAwesomeIcon icon={faUser} fixedWidth={true} />{' '}
						{getValues()['Addresses'][props.fieldIndex].name}
					</div>
					<div>
						<FontAwesomeIcon icon={faPhone} fixedWidth={true} />{' '}
						{getValues()['Addresses'][props.fieldIndex].phoneNo}
					</div>
					<div>
						<FontAwesomeIcon icon={faEnvelope} fixedWidth={true} />{' '}
						{getValues()['Addresses'][props.fieldIndex].emailAddress}
					</div>
					<StackHorizontal align={FlexAlign.Center} gap={FlexGap.ExtraSmall}>
						<FontAwesomeIcon icon={faLocationDot} fixedWidth={true} />
						<div>
							{getValues()['Addresses'][props.fieldIndex].businessName
								? getValues()['Addresses'][props.fieldIndex].businessName + ', '
								: ''}
							{getValues()['Addresses'][props.fieldIndex].propertyName
								? getValues()['Addresses'][props.fieldIndex].propertyName + ', '
								: ''}
							{getValues()['Addresses'][props.fieldIndex].addressLine1 + ', '}
							{getValues()['Addresses'][props.fieldIndex].addressLine2 + ', '}
							{getValues()['Addresses'][props.fieldIndex].addressLine3
								? getValues()['Addresses'][props.fieldIndex].addressLine3 + ', '
								: ''}
							{getValues()['Addresses'][props.fieldIndex].postCode + ', '}
							{getValues()['Addresses'][props.fieldIndex].countryCode}
						</div>
					</StackHorizontal>
				</div>

				{!isLarge && (
					<StackVertical gap={FlexGap.Small} align={FlexAlign.Start}>
						<Pill content={'Valid'} colour={ColourSolid.Success} size={PillSize.Small} />
						<Button
							title={'Remove'}
							colour={ColourMod.Alert}
							onClick={() => HandleRemove(props.fieldIndex)}
							icon={<FontAwesomeIcon icon={faTrash} />}
							iconOnly={true}
							fullWidth={!isLarge}
							squared={true}
						/>
					</StackVertical>
				)}

				{isLarge && (
					<StackHorizontal gap={FlexGap.Small} justify={FlexJustify.End} extraClass={isLarge ? '' : 'mt-sm'}>
						<Pill content={'Valid'} colour={ColourSolid.Success} size={PillSize.Small} />
						<Button
							title={'Remove'}
							colour={ColourMod.Alert}
							onClick={() => HandleRemove(props.fieldIndex)}
							icon={<FontAwesomeIcon icon={faTrash} />}
							fullWidth={!isLarge}
							squared={true}
						/>
					</StackHorizontal>
				)}
			</div>
		);
	}

	return (
		<div
			className={`address-row-requires-attention ${!props.embedded ? 'm-sm' : ''}`}
			data-row-id={props.fieldIndex}
		>
			<div
				className={'item-row item-row-static item-row-table'}
				style={{ gridTemplateColumns: !isLarge ? '1fr' : '1fr 5em' }}
			>
				<div
					style={{
						display: 'grid',
						gap: 'var(--sm-unit)',
						gridTemplateRows: 'auto',
						gridTemplateColumns: isXL ? 'repeat(5, 1fr)' : isMedium ? 'repeat(3, 1fr)' : 'repeat(1, 1fr)',
					}}
				>
					<FormWrapperLabel
						label={'Name'}
						value={props.name}
						invalid={
							formState.errors.Addresses &&
							formState.errors.Addresses[props.fieldIndex]?.name &&
							formState.errors.Addresses[props.fieldIndex]?.name?.message !== ''
						}
						validationMessage={
							formState.errors.Addresses
								? (formState.errors.Addresses[props.fieldIndex]?.name?.message as string)
								: ''
						}
					>
						<input
							type='text'
							aria-invalid={
								formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.name
									? 'true'
									: 'false'
							}
							defaultValue={props.name}
							{...register(`Addresses.${props.fieldIndex}.name`, {
								required: 'This is a required field',
							})}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'Business Name'}
						value={props.businessName}
						invalid={
							formState.errors.Addresses &&
							formState.errors.Addresses[props.fieldIndex]?.businessName &&
							formState.errors.Addresses[props.fieldIndex]?.businessName?.message !== ''
						}
						validationMessage={
							formState.errors.Addresses
								? formState.errors.Addresses &&
								  (formState.errors.Addresses[props.fieldIndex]?.businessName?.message as string)
								: ''
						}
					>
						<input
							type='text'
							aria-invalid={
								formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.businessName
									? 'true'
									: 'false'
							}
							defaultValue={props.businessName}
							{...register(`Addresses.${props.fieldIndex}.businessName`)}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'Property Name/Number'}
						value={props.propertyName}
						invalid={
							formState.errors.Addresses &&
							formState.errors.Addresses[props.fieldIndex]?.propertyName &&
							formState.errors.Addresses[props.fieldIndex]?.propertyName?.message !== ''
						}
						validationMessage={
							formState.errors.Addresses
								? (formState.errors.Addresses[props.fieldIndex]?.propertyName?.message as string)
								: ''
						}
					>
						<input
							type='text'
							aria-invalid={
								formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.propertyName
									? 'true'
									: 'false'
							}
							defaultValue={props.propertyName}
							{...register(`Addresses.${props.fieldIndex}.propertyName`)}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'Street'}
						value={props.addressLine1}
						invalid={
							formState.errors.Addresses &&
							formState.errors.Addresses[props.fieldIndex]?.addressLine1 &&
							formState.errors.Addresses[props.fieldIndex]?.addressLine1?.message !== ''
						}
						validationMessage={
							formState.errors.Addresses
								? (formState.errors.Addresses[props.fieldIndex]?.addressLine1?.message as string)
								: ''
						}
					>
						<input
							type='text'
							aria-invalid={
								formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.addressLine1
									? 'true'
									: 'false'
							}
							defaultValue={props.addressLine1}
							{...register(`Addresses.${props.fieldIndex}.addressLine1`, {
								required: 'This is a required field',
							})}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'City'}
						value={props.addressLine2}
						invalid={
							formState.errors.Addresses &&
							formState.errors.Addresses[props.fieldIndex]?.addressLine2 &&
							formState.errors.Addresses[props.fieldIndex]?.addressLine2?.message !== ''
						}
						validationMessage={
							formState.errors.Addresses
								? (formState.errors.Addresses[props.fieldIndex]?.addressLine2?.message as string)
								: ''
						}
					>
						<input
							type='text'
							aria-invalid={
								formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.addressLine2
									? 'true'
									: 'false'
							}
							defaultValue={props.addressLine2}
							{...register(`Addresses.${props.fieldIndex}.addressLine2`, {
								required: 'This is a required field',
							})}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'Locality'}
						value={props.addressLine3}
						invalid={
							formState.errors.Addresses &&
							formState.errors.Addresses[props.fieldIndex]?.addressLine3 &&
							formState.errors.Addresses[props.fieldIndex]?.addressLine3?.message !== ''
						}
						validationMessage={
							formState.errors.Addresses
								? (formState.errors.Addresses[props.fieldIndex]?.addressLine3?.message as string)
								: ''
						}
					>
						<input
							type='text'
							aria-invalid={
								formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.addressLine3
									? 'true'
									: 'false'
							}
							defaultValue={props.addressLine3}
							{...register(`Addresses.${props.fieldIndex}.addressLine3`)}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'Post Code'}
						value={props.postCode}
						invalid={
							formState.errors.Addresses &&
							formState.errors.Addresses[props.fieldIndex]?.postCode &&
							formState.errors.Addresses[props.fieldIndex]?.postCode?.message !== ''
						}
						validationMessage={
							formState.errors.Addresses
								? (formState.errors.Addresses[props.fieldIndex]?.postCode?.message as string)
								: ''
						}
					>
						<input
							type='text'
							aria-invalid={
								formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.postCode
									? 'true'
									: 'false'
							}
							defaultValue={props.postCode}
							{...register(`Addresses.${props.fieldIndex}.postCode`, {
								required: 'This is a required field',
							})}
						/>
					</FormWrapperLabel>

					<Controller
						control={control}
						name={`Addresses.${props.fieldIndex}.countryCode`}
						defaultValue={props.countryCode}
						rules={{ required: 'Country is required' }}
						render={({ field, fieldState }) => (
							<CountryCodeControl
								name={'Country'}
								label={'Country'}
								placeholder={'Country'}
								onChange={(val: CountryCodeEntryType) => field.onChange(val.value)}
								error={
									formState.errors.Addresses
										? (formState.errors.Addresses[props.fieldIndex]?.countryCode?.message as string)
										: ''
								}
								defaultValue={props.countryCode}
							/>
						)}
					/>

					<TelephoneControl
						label={'Telephone Number'}
						name={`Addresses.${props.fieldIndex}.phoneNo`}
						fieldIndex={props.fieldIndex}
						defaultValue={props.phoneNo}
						forwardStatus={(e: any) => props.lookUpsActive(e)}
					/>

					<EmailControl
						control={control}
						name={`Addresses.${props.fieldIndex}.emailAddress`}
						invalid={
							formState.errors.Addresses &&
							formState.errors.Addresses[props.fieldIndex]?.emailAddress &&
							formState.errors.Addresses[props.fieldIndex]?.emailAddress?.message !== ''
						}
						validationMessage={
							formState.errors.Addresses
								? (formState.errors.Addresses[props.fieldIndex]?.emailAddress?.message as string)
								: ''
						}
						defaultValue={props.emailAddress}
						checkUnique={false}
						setError={setError}
						clearErrors={clearErrors}
					/>
				</div>

				<StackHorizontal gap={FlexGap.Small} justify={FlexJustify.End} extraClass={isLarge ? '' : 'mt-lg'}>
					<Button
						title={'Remove'}
						colour={ColourMod.Alert}
						onClick={() => HandleRemove(props.fieldIndex)}
						icon={<FontAwesomeIcon icon={faTrash} />}
						iconOnly={isLarge}
						fullWidth={!isLarge}
						squared={true}
					/>
					{/*     <Button title={'Edit'} disabled={!props.validRow} fullWidth={true} onClick={() => setEditing(!editing)} icon={<FontAwesomeIcon icon={faPencil} />} iconOnly={true} squared={true} />
					 */}{' '}
				</StackHorizontal>
			</div>
		</div>
	);
};

export default AddressImporterRow;
